import { useEffect, useId, useRef, useState } from 'react'
import { AYUDAS_DE_CAMPO } from './contenidoDeTips'
import './tips.css'

/**
 * RF-1704 · El «?» junto a un campo difícil. Al pedirlo explica qué va ahí y de dónde se saca; se cierra con el
 * mismo botón, con Esc o con un clic fuera. Sigue disponible con «Mostrar TIPs» apagado: solo aparece si el
 * usuario lo pide, así que no estorba (RF-1707).
 *
 * Patrón de divulgación (WAI-ARIA): botón con `aria-expanded` y `aria-controls` sobre la explicación. Una clave
 * sin contenido no pinta nada: un «?» que no explica nada sería peor que ninguno.
 */
export function AyudaDeCampo({ clave }: { clave: string }) {
  const contenido = AYUDAS_DE_CAMPO[clave]
  const [abierta, setAbierta] = useState(false)
  const idExplicacion = useId()
  const raiz = useRef<HTMLSpanElement>(null)
  const boton = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!abierta) return
    const alPresionar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Esc cierra esta ayuda y no el formulario o el modal en que vive.
      e.preventDefault()
      e.stopPropagation()
      setAbierta(false)
      boton.current?.focus()
    }
    const alHacerClic = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAbierta(false)
    }
    document.addEventListener('keydown', alPresionar, true)
    document.addEventListener('mousedown', alHacerClic)
    return () => {
      document.removeEventListener('keydown', alPresionar, true)
      document.removeEventListener('mousedown', alHacerClic)
    }
  }, [abierta])

  if (!contenido) return null

  return (
    <span className="ayuda-de-campo" ref={raiz}>
      <button
        ref={boton}
        type="button"
        className="ayuda-de-campo-boton"
        aria-expanded={abierta}
        aria-controls={idExplicacion}
        aria-label={`Ayuda: ${contenido.titulo}`}
        onClick={(e) => {
          // Dentro de un <label>, el clic no debe llevar el foco al campo ni enviar el formulario.
          e.preventDefault()
          setAbierta((a) => !a)
        }}
      >
        ?
      </button>
      <span id={idExplicacion} className="ayuda-de-campo-globo" hidden={!abierta}>
        <strong>{contenido.titulo}.</strong> {contenido.texto}
      </span>
    </span>
  )
}
